import { WIZARD_QUESTION_IDS } from '../../../constants.js';
import type { WorldDraft } from '../../../domain/types.js';
import { OPERATING_SCHEDULE_SCHEMA_VERSION } from '../../../domain/types.js';
import { compilePromptRequest } from '../../../prompts/compile.js';
import type { CharacterOptionsOutput } from '../../../prompts/schemas.js';
import type { ActorContext, PromptSourceBundle } from '../../../prompts/types.js';
import { executeDirectorPrompt } from '../../../providers/director.js';
import { deepSeekReady } from '../../../llm/client.js';
import { loadPersistedKey } from '../../secureStore.js';
import { defaultSchedule } from '../fallbacks.js';
import {
  appearanceCatalogKeyForDraft,
  catalogFromDirectorOutput,
  seededAppearanceCatalog,
  type AppearanceCatalog,
  type AppearanceCatalogSource,
} from './appearanceOptions.js';

export const APPEARANCE_CATALOG_STORAGE_KEY = 'specimen-observer:appearance-catalog-cache';
export const APPEARANCE_CATALOG_CACHE_VERSION = 1;

const CATALOG_KEY_PREFIX = `${APPEARANCE_CATALOG_STORAGE_KEY}:catalog:`;

/**
 * Future SQLite migration (v4, flagged — not applied automatically):
 *
 * CREATE TABLE IF NOT EXISTS appearance_catalog_cache (
 *   cache_key TEXT PRIMARY KEY,
 *   species TEXT NOT NULL,
 *   region TEXT NOT NULL,
 *   period TEXT NOT NULL,
 *   catalog_json TEXT NOT NULL,
 *   source TEXT NOT NULL,
 *   frozen_at TEXT NOT NULL DEFAULT (datetime('now'))
 * );
 * CREATE INDEX IF NOT EXISTS idx_appearance_catalog_species ON appearance_catalog_cache(species);
 */

export interface CachedAppearanceCatalog {
  version: number;
  key: string;
  catalog: AppearanceCatalog;
  source: AppearanceCatalogSource;
  frozenAt: string;
}

export interface AppearanceCatalogCache {
  get(key: string): CachedAppearanceCatalog | null;
  freeze(key: string, catalog: AppearanceCatalog, source: AppearanceCatalogSource): CachedAppearanceCatalog;
  has(key: string): boolean;
  clear(): void;
}

/** Per-slug localStorage key — each frozen set is isolated for atomic insert-only writes. */
export function catalogStorageKey(slug: string): string {
  return `${CATALOG_KEY_PREFIX}${slug}`;
}

function slugFromStorageKey(storageKey: string): string {
  return storageKey.slice(CATALOG_KEY_PREFIX.length);
}

function isCatalogStorageKey(storageKey: string | null): storageKey is string {
  return storageKey?.startsWith(CATALOG_KEY_PREFIX) ?? false;
}

function parseEntry(raw: string): CachedAppearanceCatalog | null {
  try {
    const parsed = JSON.parse(raw) as CachedAppearanceCatalog;
    return parsed && typeof parsed === 'object' && typeof parsed.key === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function readEntryFromStorageKey(storageKey: string): CachedAppearanceCatalog | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return parseEntry(raw);
  } catch {
    return null;
  }
}

/** Import legacy whole-object blob into per-key entries, then remove the legacy key. */
function migrateLegacyStore(): void {
  try {
    const raw = localStorage.getItem(APPEARANCE_CATALOG_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, CachedAppearanceCatalog>;
    if (!parsed || typeof parsed !== 'object') return;
    for (const [slug, entry] of Object.entries(parsed)) {
      const storageKey = catalogStorageKey(slug);
      if (!localStorage.getItem(storageKey)) {
        localStorage.setItem(storageKey, JSON.stringify(entry));
      }
    }
    localStorage.removeItem(APPEARANCE_CATALOG_STORAGE_KEY);
  } catch {
    // Retry on next read when storage is temporarily unavailable.
  }
}

function loadAllEntries(): Map<string, CachedAppearanceCatalog> {
  migrateLegacyStore();
  const map = new Map<string, CachedAppearanceCatalog>();
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const storageKey = localStorage.key(i);
      if (!isCatalogStorageKey(storageKey)) continue;
      const entry = readEntryFromStorageKey(storageKey);
      if (entry) map.set(slugFromStorageKey(storageKey), entry);
    }
  } catch {
    // Session-only cache when storage is unavailable.
  }
  return map;
}

/** Insert-only on a single storage key — concurrent tabs freezing different slugs never merge. */
function persistIfAbsent(slug: string, entry: CachedAppearanceCatalog): CachedAppearanceCatalog {
  const storageKey = catalogStorageKey(slug);
  const existing = readEntryFromStorageKey(storageKey);
  if (existing) return existing;
  try {
    localStorage.setItem(storageKey, JSON.stringify(entry));
  } catch {
    // Session-only cache when storage is unavailable.
  }
  return readEntryFromStorageKey(storageKey) ?? entry;
}

function removeAllCatalogKeys(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const storageKey = localStorage.key(i);
      if (isCatalogStorageKey(storageKey)) keysToRemove.push(storageKey);
    }
    for (const storageKey of keysToRemove) {
      localStorage.removeItem(storageKey);
    }
    localStorage.removeItem(APPEARANCE_CATALOG_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export class LocalAppearanceCatalogCache implements AppearanceCatalogCache {
  private store: Map<string, CachedAppearanceCatalog>;

  constructor(seed?: Map<string, CachedAppearanceCatalog>) {
    this.store = seed ?? loadAllEntries();
  }

  /** Reload all catalog entries from localStorage (cross-tab bulk sync). */
  refreshFromStorage(): void {
    this.store = loadAllEntries();
  }

  /** Refresh one slug after a cross-tab storage event for its per-key entry. */
  refreshEntryFromStorage(slug: string): void {
    migrateLegacyStore();
    const entry = readEntryFromStorageKey(catalogStorageKey(slug));
    if (entry) {
      this.store.set(slug, entry);
    } else {
      this.store.delete(slug);
    }
  }

  get(key: string): CachedAppearanceCatalog | null {
    return this.store.get(key) ?? null;
  }

  has(key: string): boolean {
    return this.store.has(key);
  }

  /** Insert-only — frozen catalogs are never overwritten. Uses per-key write-if-absent. */
  freeze(
    key: string,
    catalog: AppearanceCatalog,
    source: AppearanceCatalogSource,
  ): CachedAppearanceCatalog {
    const existing = this.store.get(key);
    if (existing) return existing;
    const entry: CachedAppearanceCatalog = {
      version: APPEARANCE_CATALOG_CACHE_VERSION,
      key,
      catalog,
      source,
      frozenAt: new Date().toISOString(),
    };
    const persisted = persistIfAbsent(key, entry);
    this.store.set(key, persisted);
    return persisted;
  }

  clear(): void {
    this.store = new Map();
    removeAllCatalogKeys();
  }
}

let defaultCache: AppearanceCatalogCache = new LocalAppearanceCatalogCache();

function handleCrossTabStorageEvent(event: StorageEvent): void {
  if (!(defaultCache instanceof LocalAppearanceCatalogCache)) return;
  if (event.key === APPEARANCE_CATALOG_STORAGE_KEY) {
    defaultCache.refreshFromStorage();
    return;
  }
  if (isCatalogStorageKey(event.key)) {
    defaultCache.refreshEntryFromStorage(slugFromStorageKey(event.key));
  }
}

/**
 * Subscribe to cross-tab localStorage updates; no-op outside a DOM environment.
 * Per-key entries: any storage event whose key matches the catalog prefix refreshes that slug.
 */
export function subscribeAppearanceCatalogCrossTabSync(): () => void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }
  window.addEventListener('storage', handleCrossTabStorageEvent);
  return () => window.removeEventListener('storage', handleCrossTabStorageEvent);
}

subscribeAppearanceCatalogCrossTabSync();

export function getAppearanceCatalogCache(): AppearanceCatalogCache {
  return defaultCache;
}

/** Test hook — swap cache implementation without touching production storage. */
export function setAppearanceCatalogCache(cache: AppearanceCatalogCache): void {
  defaultCache = cache;
}

export function resetAppearanceCatalogCache(): void {
  defaultCache = new LocalAppearanceCatalogCache();
}

export function buildCharacterOptionsSource(
  draft: WorldDraft,
  species: string,
  draftCharacterId: string,
  sex: 'M' | 'F' = 'M',
): PromptSourceBundle {
  const location = draft.location ?? { name: '', region: '', language: 'English' };
  const connected = draft.connected ?? {
    locationType: '',
    description: '',
    crowd: { type: 'public_venue' as const },
    rhythm: { skipped: true },
  };
  const timePeriod = draft.timePeriod ?? { present: 'Present day' };
  const schedule = draft.schedule ?? defaultSchedule(null, draft.draftId);
  const actor: ActorContext = {
    actorId: draftCharacterId,
    characterVersion: 1,
    firstName: 'Draft',
    displayName: 'Draft',
    species,
    sex,
    ageRange: 'Adult',
    traits: [],
    goals: [],
    relationships: [],
    durableMemories: [],
  };

  return {
    worldId: draft.draftId,
    worldVersion: 1,
    locationType: 'other',
    location,
    connected,
    answers: {
      [WIZARD_QUESTION_IDS.groundingName]: {
        questionId: WIZARD_QUESTION_IDS.groundingName,
        schemaVersion: '2.0.0',
        value: location.name,
        status: 'confirmed',
        source: 'user',
        dependencies: [],
        updatedAt: new Date().toISOString(),
      },
    },
    timePeriod,
    schedule: { ...schedule, schemaVersion: schedule.schemaVersion ?? OPERATING_SCHEDULE_SCHEMA_VERSION },
    assumptions: [],
    clock: { inWorldIso: new Date().toISOString(), episodeNumber: 1 },
    operatingStatus: 'open',
    mode: 'creating',
    confirmedSnapshot: true,
    activeDevices: [],
    inactiveDevices: [],
    allActors: [actor],
    selectedActorIds: [draftCharacterId],
    nearbyActorIds: [],
    events: [],
    summaries: [],
    generationSeed: draft.draftId,
    locale: 'en-US',
  };
}

export interface ResolveAppearanceCatalogParams {
  draft: WorldDraft;
  species: string;
  draftCharacterId: string;
  sex?: 'M' | 'F';
  cache?: AppearanceCatalogCache;
  loadKey?: () => Promise<string>;
  fetchOptions?: (
    source: PromptSourceBundle,
    draftCharacterId: string,
  ) => Promise<CharacterOptionsOutput | null>;
}

async function defaultFetchOptions(
  source: PromptSourceBundle,
  draftCharacterId: string,
): Promise<CharacterOptionsOutput | null> {
  const request = compilePromptRequest('character_options', source, { draftActorId: draftCharacterId });
  const result = await executeDirectorPrompt<CharacterOptionsOutput>(request);
  if (!result.ok) return null;
  return result.data;
}

export async function resolveAppearanceCatalog(
  params: ResolveAppearanceCatalogParams,
): Promise<{ catalog: AppearanceCatalog; source: AppearanceCatalogSource; key: string }> {
  const cache = params.cache ?? getAppearanceCatalogCache();
  const key = appearanceCatalogKeyForDraft(params.draft, params.species, params.sex);
  const cached = cache.get(key);
  if (cached) {
    return { catalog: cached.catalog, source: 'frozen' as const, key };
  }

  const loadKey = params.loadKey ?? (() => loadPersistedKey('deepseek'));
  const savedKey = (await loadKey()).trim();
  if (!deepSeekReady() && !savedKey) {
    const catalog = seededAppearanceCatalog(key, params.species);
    return { catalog, source: 'seeded', key };
  }

  const source = buildCharacterOptionsSource(params.draft, params.species, params.draftCharacterId, params.sex ?? 'M');
  const fetchOptions = params.fetchOptions ?? defaultFetchOptions;
  try {
    const output = await fetchOptions(source, params.draftCharacterId);
    if (!output) {
      const catalog = seededAppearanceCatalog(key, params.species);
      return { catalog, source: 'seeded', key };
    }
    const catalog = catalogFromDirectorOutput(output, params.species, params.sex);
    cache.freeze(key, catalog, 'ai');
    return { catalog, source: 'ai', key };
  } catch {
    const catalog = seededAppearanceCatalog(key, params.species);
    return { catalog, source: 'seeded', key };
  }
}
